/**
 * ContinuityRibbon — §23 Step 15.
 *
 * The quiet "What changed?" affordance. Renders at most 3 of the
 * most recent confirmation-receipt-style continuity events from
 * the backend (`payload.recent_continuity`).
 *
 * Doctrine rules baked in:
 *   • Server-owned wording. The frontend renders `message`
 *     verbatim — never invents text, never appends "today",
 *     never reformats the timestamp.
 *   • Silence is the calm default (§18). When the list is empty
 *     this component returns null — no header, no placeholder.
 *   • No counts, no scores, no badges, no streaks, no XP.
 *   • Not a dashboard. Subtle treatment: small text, low contrast.
 *   • Visually does NOT dominate the home — sits below the engine
 *     slots, above the legacy banner stack.
 */
import { useMemo } from "react";

function _fmtWhen(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  } catch {
    return null;
  }
}

export default function ContinuityRibbon({ items }) {
  const rows = useMemo(
    () => (Array.isArray(items) ? items.slice(0, 3) : []),
    [items],
  );
  if (!rows.length) return null;

  return (
    <section
      data-testid="learner-state-continuity-ribbon"
      aria-label="What changed recently"
      className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-900/40 px-4 py-3"
    >
      <p
        data-testid="continuity-ribbon-heading"
        className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-400 dark:text-slate-500"
      >
        What changed
      </p>
      <ul className="mt-2 space-y-1.5">
        {rows.map((row) => {
          const when = _fmtWhen(row?.occurred_at);
          return (
            <li
              key={row?.id || row?.message}
              data-testid={`continuity-ribbon-item-${row?.id || ""}`}
              className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed"
            >
              <span>{row?.message}</span>
              {when && (
                <span className="ml-2 text-[11px] text-slate-400 dark:text-slate-500">
                  {when}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
